import { Box, Link, Stack, Typography } from '@mui/material'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { ArrowLeft, Clock } from 'lucide-react'
import { getArticle } from '@/content/articles'
import { productImagesFor } from '@/content/products'
import { brand } from '@/content/site'
import { colors } from '@/theme'
import { Section } from '@/components/ui/Section'
import { Eyebrow } from '@/components/ui/Eyebrow'
import { Breadcrumbs } from '@/components/ui/Breadcrumbs'
import { Media } from '@/components/ui/Media'
import { ContactCTA } from '@/components/sections/ContactCTA'
import { Seo } from '@/components/seo/Seo'
import { JsonLd } from '@/components/seo/JsonLd'
import { NotFound } from './NotFound'

export function Article() {
  const { slug } = useParams()
  const article = getArticle(slug)

  if (!article) return <NotFound />

  const gallery = productImagesFor(article.image.sku)
  const img = gallery[article.image.index] ?? gallery[0]

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    articleSection: article.category,
    image: img ? `${brand.url}${img.src}` : `${brand.url}${brand.logoBadge}`,
    author: { '@type': 'Organization', name: brand.legalName },
    publisher: { '@type': 'Organization', name: brand.legalName },
  }

  return (
    <>
      <Seo title={article.title} path={`/articles/${article.slug}`} description={article.excerpt} image={img?.src} />
      <JsonLd data={schema} />

      <Section sx={{ pb: 0 }}>
        <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Articles', to: '/articles' }, { label: article.title }]} />
        <Eyebrow>{article.category}</Eyebrow>
        <Typography variant="h1" component="h1" sx={{ maxWidth: 900 }}>
          {article.title}
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2.5, color: 'text.secondary' }}>
          <Typography variant="body2">{article.date}</Typography>
          <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: colors.grey }} />
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Clock size={14} />
            <Typography variant="body2">{article.readMins} min read</Typography>
          </Stack>
        </Stack>
      </Section>

      {img && (
        <Section sx={{ py: { xs: 4, md: 5 } }}>
          <Box sx={{ borderRadius: 1, overflow: 'hidden', border: `1px solid ${colors.border}`, maxWidth: 960, mx: 'auto' }}>
            <Media src={img.src} srcSm={img.srcSm} alt={article.title} width={img.width} height={img.height} ratio={16 / 9} eager />
          </Box>
        </Section>
      )}

      <Section sx={{ pt: 0 }}>
        <Box sx={{ maxWidth: 720 }}>
          {article.sections.map((s, i) => (
            <Box key={i} sx={{ mb: { xs: 3.5, md: 4.5 } }}>
              {s.heading && (
                <Typography variant="h4" component="h2" sx={{ mb: 2, mt: { xs: 1, md: 2 } }}>
                  {s.heading}
                </Typography>
              )}
              {s.body.map((p, j) => (
                <Typography key={j} variant="body1" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.8 }}>
                  {p}
                </Typography>
              ))}
            </Box>
          ))}

          <Link component={RouterLink} to="/articles" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: 'primary.main', fontWeight: 600, mt: 2 }}>
            <ArrowLeft size={18} /> All articles
          </Link>
        </Box>
      </Section>

      <ContactCTA />
    </>
  )
}
